import isNull from 'lodash/isNull'
import omitBy from 'lodash/omitBy'
import type { RouteObject } from 'react-router-dom'
import { Navigate, Outlet } from 'react-router-dom'

import ListPage from '@app/pages/entities/list'
import StrategyShow from '@app/pages/entities/show'
import Tabs from '@app/pages/entities/tabs'
import EntityDetails from '@app/pages/entities/tabs/entityDetails'
import EntityFormTab from '@app/pages/entities/tabs/entityFormTab'
import Create from '@app/pages/shared/entities/create'
import Edit from '@app/pages/shared/entities/edit'
import List from '@app/pages/shared/entities/list'
import Show from '@app/pages/shared/entities/show'
import ImpactGraphs from '@app/pages/shared/impactGraphs/impactGraphs'
import CommentsFetcher from '@app/shared/comments/commentsFetcher'
import extractOrderings from '@app/shared/utils/extractOrderings'
import { useStore } from '@app/store'
import type { Nodes } from '@app/types'
import { requiresAuthorization } from '@app/utils/auth'
import { Entities, EntityForm } from '@graphql/documents/entity.graphql'
import type { EntitiesQuery, EntitiesQueryVariables } from '@graphql/queries'

const loadEntities = async ({ request }) => {
  const url = new URL(request.url)
  const page = parseInt(url.searchParams.get('page'), 10) || 1
  const limit = parseInt(url.searchParams.get('limit'), 10) || null
  const filter = url.searchParams.get('filter')
  const strategyId = url.searchParams.get('strategyId')
  const order = extractOrderings(url.searchParams, ['name', 'sourceName', 'foreignType'])
  const { loaderQuery } = useStore.getState()

  const variables: EntitiesQueryVariables = omitBy({ page, limit, filter, order, strategyId }, isNull)

  const result = await loaderQuery<EntitiesQuery, EntitiesQueryVariables>(Entities, variables)
  const { addObjectPage } = useStore.getState()

  const { collection = [], metadata } = result.data.entities
  addObjectPage('entity', collection, metadata)

  return result.data
}

const loadEntity = async ({ params }) => {
  const { loaderQuery } = useStore.getState()

  const { data } = await loaderQuery(EntityForm, {
    id: params.entityId || params.workId || params.nodeId
  })

  const { entity } = data

  return { entity }
}

const SharedEventRoutes = (containerType: Nodes) => [
  {
    index: true,
    element: <List containerType={containerType} />
  },
  {
    path: 'new',
    loader: async () => requiresAuthorization('create', 'event'),
    element: <Create containerType={containerType} />
  },
  {
    path: ':entityId',
    element: <Show />
  },
  {
    // Should this be nested under <Show />?
    path: ':entityId/edit',
    loader: async (params) => {
      await requiresAuthorization('update', 'event')

      return loadEntity(params)
    },
    element: <Edit containerType={containerType} />
  }
]

const entityShowTabRoutes = (namespace = null) => [
  {
    path: ':nodeId',
    children: [
      {
        path: '*',
        id: namespace ? `${namespace}-entity` : 'entity',
        loader: loadEntity,
        element: <StrategyShow />,
        children: [
          {
            index: true,
            element: <Navigate to="details" replace />
          },
          {
            element: <Tabs />,
            children: [
              {
                path: 'details',
                element: <EntityDetails />
              },
              {
                path: 'events',
                children: SharedEventRoutes('entity')
              },
              {
                path: 'impact',
                children: [
                  {
                    index: true,
                    element: <ImpactGraphs nodeType="entity" />
                  }
                ]
              },
              {
                path: 'comments',
                element: <CommentsFetcher commentableType="entity" />
              },
              {
                path: 'settings',
                loader: async () => requiresAuthorization('update', 'entity'),
                element: <EntityFormTab />
              }
            ]
          }
        ]
      }
    ]
  }
]

const workShowTabRoutes = (namespace = null) => [
  {
    path: ':nodeId',
    children: [
      {
        path: '*',
        id: namespace ? `${namespace}-work` : 'work',
        loader: loadEntity,
        element: <StrategyShow />,
        children: [
          {
            index: true,
            element: <Navigate to="events" replace />
          },
          {
            element: <Tabs />,
            children: [
              {
                path: 'events',
                children: SharedEventRoutes('entity')
              }
            ]
          }
        ]
      }
    ]
  }
]

const routes: RouteObject = {
  path: 'work',
  element: <Outlet />,
  children: [
    {
      index: true,
      loader: loadEntities,
      element: <ListPage />
    }
  ]
}

export { SharedEventRoutes, entityShowTabRoutes, workShowTabRoutes }

export default routes
